import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { usePopupFormContext } from "../../../../hooks/usePopupFormContext";
import { findVariantByFormValue } from "../../../../layouts/product-recommendation/utils/product-variant-utils.js";
import { parseCssStringToObject } from "../../../../helper";
import { DEFAULT_BLOCK_CSS, POPUP_SECTIONS } from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import {
  COVER_IMAGE_LAYOUT,
  getCoverImageCatalogSizes,
  getCoverImageDisplay,
  getVariantImageIndex,
  resolveCoverImageUrls,
  resolveVariantImageUrl,
} from "../../../../../shared/product-block-display.js";
import {
  getProductRecommendationHeroImageHeight,
  getProductRecommendationImageSlotHeight,
} from "../../../../../shared/product-recommendation-popup-schema.js";
import {
  buildCoverImageFrameStyle,
  formatBorderRadiusCss,
  parseBorderRadiusPx,
  resolvePopupThemeBorderRadius,
} from "../../../../utils/resolve-popup-theme-border-radius.js";
import { isCartDrawerVerticalShell } from "../../../../../shared/product-shell-layout.js";
import styles from "./CoverImageBlock.module.css";
import CatalogDummyImagePlaceholder from "../../catalog-product/core/CatalogDummyImagePlaceholder";
import useProductLink from "../../../../hooks/useProductLink";
import {
  CATALOG_CARD_CDN_WIDTH,
  CATALOG_THUMB_CDN_WIDTH,
  getShopifyCdnSizedImageUrl,
  resolveShopifyCdnWidthFromDisplay,
} from "../../../../../shared/shopify-cdn-image.js";

function ProductImageLinkShell({
  isClickable,
  navigate,
  className,
  style,
  children,
  onMouseMove,
  onMouseLeave,
  onMouseEnter,
}) {
  const hoverProps = { onMouseMove, onMouseLeave, onMouseEnter };

  if (!isClickable) {
    return (
      <div className={className} style={style} {...hoverProps}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ ...style, cursor: "pointer" }}
      role="link"
      tabIndex={0}
      onClick={navigate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          navigate(event);
        }
      }}
      {...hoverProps}
    >
      {children}
    </div>
  );
}

function normalizeImageSrc(src, width = CATALOG_CARD_CDN_WIDTH) {
  const raw = String(src || "").trim();
  if (!raw) return "";
  const withProtocol = raw.startsWith("//")
    ? `${typeof window !== "undefined" ? window.location.protocol : "https:"}${raw}`
    : raw;
  return getShopifyCdnSizedImageUrl(withProtocol, width);
}

/** Fade-in cover image with skeleton until CDN finishes (avoids blank flash). */
function SizedCoverImg({
  src,
  alt = "Product",
  className,
  style,
  imgClassName,
  width,
  height,
  objectFit,
  objectPosition,
}) {
  const [status, setStatus] = useState(src ? "loading" : "empty");

  useEffect(() => {
    setStatus(src ? "loading" : "empty");
  }, [src]);

  const bindImg = useCallback((node) => {
    if (!node) return;
    if (node.complete && node.naturalWidth > 0) {
      setStatus("loaded");
    }
  }, []);

  if (!src) return null;

  return (
    <span className={className} style={{ position: "relative", display: "block", width: "100%", height: "100%", ...style }}>
      {status === "loading" ? (
        <span className={styles.imageSkeleton} aria-hidden style={{ position: "absolute", inset: 0 }} />
      ) : null}
      <img
        ref={bindImg}
        key={src}
        src={src}
        alt={alt}
        className={`${imgClassName || ""}${status === "loaded" ? ` ${styles.coverImgLoaded}` : ` ${styles.coverImgPending}`}`.trim()}
        width={width}
        height={height}
        loading="eager"
        decoding="async"
        fetchPriority="high"
        draggable={false}
        style={{
          objectFit: objectFit || "cover",
          objectPosition: objectPosition || "center",
        }}
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("empty")}
      />
    </span>
  );
}

function useSelectedVariant(productData, productIndex) {
  const { watch } = usePopupFormContext();
  const selectedVariantId = watch(`${POPUP_SECTIONS.VARIANT_PICKER}-${productIndex}`);

  return useMemo(() => {
    const variants = Array.isArray(productData?.variants) ? productData.variants : [];
    if (!variants.length) return null;
    return findVariantByFormValue(variants, selectedVariantId) || variants[0] || null;
  }, [productData?.variants, selectedVariantId]);
}

function useVariantImageIndex(productData, productIndex, urls) {
  const selectedVariant = useSelectedVariant(productData, productIndex);
  return useMemo(
    () => getVariantImageIndex(productData, selectedVariant, urls),
    [productData, selectedVariant, urls]
  );
}

function useVariantHeroSrc(productData, productIndex, urls, cdnWidth = CATALOG_CARD_CDN_WIDTH) {
  const selectedVariant = useSelectedVariant(productData, productIndex);

  return useMemo(() => {
    const fromVariant = resolveVariantImageUrl(productData, selectedVariant);
    if (fromVariant) return normalizeImageSrc(fromVariant, cdnWidth);
    if (!urls?.length) return "";
    return normalizeImageSrc(urls[0] || "", cdnWidth);
  }, [productData, selectedVariant, urls, cdnWidth]);
}

/** Catalog card: show main image; on card hover fade to the next product photo. */
function CatalogHoverImageSlot({
  urls,
  defaultSrc,
  imageFrameStyle,
  objectFit = "cover",
  objectPosition = "center",
  isClickable,
  navigate,
}) {
  const slotRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const uniqueUrls = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const raw of urls || []) {
      const src = String(raw || "").trim();
      if (!src || seen.has(src)) continue;
      seen.add(src);
      list.push(src);
    }
    return list;
  }, [urls]);

  // Always prefer the selected-variant image when present (even if CDN URL is
  // not an exact string match in the gallery list).
  const primarySrc = useMemo(() => {
    if (defaultSrc) return defaultSrc;
    return uniqueUrls[0] || "";
  }, [uniqueUrls, defaultSrc]);

  const hoverSrc = useMemo(() => {
    if (uniqueUrls.length < 2) return "";
    const primaryIndex = uniqueUrls.indexOf(primarySrc);
    if (primaryIndex < 0) {
      // Variant image not in gallery list — hover peeks at first different photo.
      return uniqueUrls.find((src) => src && src !== primarySrc) || "";
    }
    const next = uniqueUrls[(primaryIndex + 1) % uniqueUrls.length] || "";
    return next && next !== primarySrc ? next : "";
  }, [uniqueUrls, primarySrc]);

  const canHoverSwap = Boolean(primarySrc && hoverSrc);
  const showHover = canHoverSwap && isHovered;

  // Variant change must show the new variant image immediately — do not keep
  // the previous hover layer visible on top of it.
  useEffect(() => {
    setIsHovered(false);
  }, [primarySrc]);

  // Hover only on image + title zones — not the whole card (variants / CTAs).
  useEffect(() => {
    if (!canHoverSwap || typeof window === "undefined") return undefined;
    const slot = slotRef.current;
    if (!slot) return undefined;

    const card =
      slot.closest("[data-product-index]") ||
      slot.closest("article") ||
      null;
    const zoneSet = new Set();
    zoneSet.add(slot);
    if (card) {
      card
        .querySelectorAll("[data-catalog-hover-images-zone]")
        .forEach((el) => zoneSet.add(el));
    }
    const zones = Array.from(zoneSet);
    const isInsideZones = (node) =>
      !!node && zones.some((zone) => zone.contains(node));

    const onEnter = () => setIsHovered(true);
    const onLeave = (event) => {
      if (isInsideZones(event.relatedTarget)) return;
      setIsHovered(false);
    };

    zones.forEach((zone) => {
      zone.addEventListener("mouseenter", onEnter);
      zone.addEventListener("mouseleave", onLeave);
    });
    return () => {
      zones.forEach((zone) => {
        zone.removeEventListener("mouseenter", onEnter);
        zone.removeEventListener("mouseleave", onLeave);
      });
    };
  }, [canHoverSwap]);

  // Preload the hover image so the fade is smooth.
  useEffect(() => {
    if (!hoverSrc || typeof window === "undefined") return undefined;
    const img = new window.Image();
    img.src = hoverSrc;
    return undefined;
  }, [hoverSrc]);

  return (
    <ProductImageLinkShell
      className={`${styles.catalogSlot}${canHoverSwap ? ` ${styles.catalogHoverSlot}` : ""}`}
      isClickable={isClickable}
      navigate={navigate}
      style={imageFrameStyle}
    >
      <span ref={slotRef} className={styles.catalogHoverAnchor}>
        {canHoverSwap ? (
          <span className={styles.catalogHoverStack} key={primarySrc}>
            <img
              src={primarySrc}
              alt="Product"
              className={styles.catalogHoverLayer}
              draggable={false}
              style={{
                opacity: showHover ? 0 : 1,
                objectFit,
                objectPosition,
              }}
            />
            <img
              src={hoverSrc}
              alt=""
              aria-hidden="true"
              className={styles.catalogHoverLayer}
              draggable={false}
              style={{
                opacity: showHover ? 1 : 0,
                objectFit,
                objectPosition,
              }}
            />
          </span>
        ) : primarySrc ? (
          <SizedCoverImg
            src={primarySrc}
            alt="Product"
            imgClassName={styles.catalogSlotImg}
            objectFit={objectFit}
            objectPosition={objectPosition}
          />
        ) : (
          <CatalogDummyImagePlaceholder
            style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
            iconSize="sm"
          />
        )}
      </span>
    </ProductImageLinkShell>
  );
}

function HeroThumbnailsLayout({
  urls,
  display,
  borderRadiusPx,
  frameBorderStyle,
  objectFit,
  objectPosition,
  variantImageIndex,
  catalogSizes,
  showThumbnails = true,
  fluid = false,
  slotConstrained = false,
  catalogHeroHeight = null,
  catalogSlotHeight = null,
  preferCatalogHeroFrame = false,
  heroLinkActive = false,
  onHeroNavigate = null,
}) {
  const gap = Math.max(0, Number(display.gap) || 8);
  const thumbWidth = catalogSizes?.thumbWidth ?? 44;
  const thumbHeight = catalogSizes?.thumbHeight ?? 44;
  const useAutoImageHeight = fluid || display?.autoImageHeight === true;
  const catalogMainHeight =
    !useAutoImageHeight && catalogSizes?.mainHeight ? catalogSizes.mainHeight : null;
  const showThumbStrip = showThumbnails && urls.length > 1;
  const thumbRowReserved =
    slotConstrained && showThumbStrip ? thumbHeight + gap : 0;
  const heroFrameHeight =
    slotConstrained && catalogHeroHeight != null
      ? catalogHeroHeight
      : catalogMainHeight != null && thumbRowReserved > 0
        ? Math.max(48, catalogMainHeight - thumbRowReserved)
        : catalogMainHeight;
  const maxVisible = Math.max(3, Math.min(8, Number(display.maxThumbnailsVisible) || 5));
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (variantImageIndex >= 0 && variantImageIndex < urls.length) {
      setSelectedIndex(variantImageIndex);
    }
  }, [variantImageIndex, urls.length]);

  useEffect(() => {
    if (!display.autoScroll || urls.length < 2) return undefined;
    const intervalMs = Math.max(1500, Number(display.scrollIntervalMs) || 4000);
    const timer = window.setInterval(() => {
      setSelectedIndex((prev) => (prev + 1) % urls.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [display.autoScroll, display.scrollIntervalMs, urls.length]);

  const safeIndex = urls.length ? Math.min(selectedIndex, urls.length - 1) : 0;
  const heroSrc = urls[safeIndex] || "";
  const radius = formatBorderRadiusCss(borderRadiusPx);
  const heroFrameStyle = frameBorderStyle || { borderRadius: radius };
  const thumbRadius = formatBorderRadiusCss(Math.max(4, Math.min(borderRadiusPx, 12)));

  const wrapClass = fluid ? `${styles.heroWrap} ${styles.fluidHeroWrap}` : styles.heroWrap;
  const resolvedObjectFit =
    fluid || slotConstrained ? "contain" : objectFit;
  const useCatalogHero =
    preferCatalogHeroFrame || (slotConstrained && !fluid);
  const frameClass = fluid
    ? `${styles.heroFrame} ${styles.fluidHeroFrame}`
    : useCatalogHero
      ? `${styles.heroFrame} ${styles.catalogHeroFrame}`
      : heroFrameHeight && !showThumbnails
        ? `${styles.heroFrame} ${styles.compactSingleHeroFrame}`
        : styles.heroFrame;
  const imageClass = fluid
    ? `${styles.heroImage} ${styles.fluidHeroImage}`
    : useCatalogHero
      ? `${styles.heroImage} ${styles.catalogHeroImage}`
      : heroFrameHeight && !showThumbnails
        ? `${styles.heroImage} ${styles.compactSingleHeroImage}`
        : styles.heroImage;

  if (!urls.length) {
    return (
      <div className={wrapClass} style={{ gap }}>
        <div className={frameClass} style={heroFrameStyle}>
          <div className={styles.heroPlaceholder}>
            <span aria-hidden>📷</span>
            <span>No image available</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={wrapClass}
      style={{
        gap,
        ...(slotConstrained && catalogSlotHeight != null
          ? { maxHeight: catalogSlotHeight, height: catalogSlotHeight }
          : slotConstrained && catalogSizes?.mainHeight
            ? { maxHeight: catalogSizes.mainHeight, height: catalogSizes.mainHeight }
            : {}),
      }}
    >
      <ProductImageLinkShell
        className={frameClass}
        isClickable={heroLinkActive}
        navigate={onHeroNavigate}
        style={{
          ...heroFrameStyle,
          flex: useCatalogHero ? "0 0 auto" : "0 0 auto",
          minHeight: useCatalogHero ? 0 : undefined,
          ...(heroFrameHeight != null
            ? { height: heroFrameHeight, minHeight: heroFrameHeight }
            : useAutoImageHeight
              ? { height: "auto", minHeight: 0 }
              : showThumbStrip
                ? {}
                : { height: "auto", minHeight: 0 }),
        }}
      >
        <SizedCoverImg
          src={heroSrc}
          alt={`Product image ${safeIndex + 1}`}
          imgClassName={imageClass}
          objectFit={useCatalogHero ? "contain" : resolvedObjectFit}
          objectPosition={objectPosition}
        />
      </ProductImageLinkShell>
      {showThumbStrip ? (
        <div
          className={styles.thumbsRow}
          style={{
            gap,
            minHeight: thumbHeight,
            maxWidth: thumbWidth * maxVisible + gap * (maxVisible - 1),
          }}
          role="listbox"
          aria-label="Product images"
        >
          {urls.map((url, index) => {
            const active = index === safeIndex;
            return (
              <button
                key={`${url}-${index}`}
                type="button"
                role="option"
                aria-selected={active}
                className={`${styles.thumbButton} ${active ? styles.thumbButtonActive : ""}`}
                style={{
                  width: thumbWidth,
                  height: thumbHeight,
                  borderRadius: thumbRadius,
                }}
                onClick={() => setSelectedIndex(index)}
              >
                <SizedCoverImg
                  src={normalizeImageSrc(url, CATALOG_THUMB_CDN_WIDTH)}
                  alt=""
                  imgClassName={styles.thumbImage}
                  objectFit="cover"
                />
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function HorizontalStripLayout({
  urls,
  display,
  borderRadiusPx,
  frameBorderStyle,
  objectFit,
  objectPosition,
  catalogSizes,
  fluid = false,
  imageLinkActive = false,
  onImageNavigate = null,
}) {
  const gap = Math.max(0, Number(display.gap) || 8);
  const radius = formatBorderRadiusCss(borderRadiusPx);
  const itemFrameStyle = frameBorderStyle || { borderRadius: radius };
  const stripRef = useRef(null);
  const speed = Math.max(8, Number(display.scrollSpeed) || 32);
  const useAutoImageHeight = fluid || display?.autoImageHeight === true;

  useEffect(() => {
    if (!display.autoScroll || urls.length < 2) return undefined;
    const el = stripRef.current;
    if (!el) return undefined;

    let rafId = 0;
    let lastTs = 0;

    const tick = (ts) => {
      if (!lastTs) lastTs = ts;
      const delta = (ts - lastTs) / 1000;
      lastTs = ts;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll > 0) {
        el.scrollLeft += speed * delta;
        if (el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = 0;
        }
      }
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [display.autoScroll, speed, urls.length]);

  if (!urls.length) {
    return (
      <div className={styles.stripOuter}>
        <div className={styles.heroPlaceholder} style={itemFrameStyle}>
          <span aria-hidden>📷</span>
          <span>No image available</span>
        </div>
      </div>
    );
  }

  const slideWidthPercent = urls.length === 1 ? 100 : 78;

  const stripHeight =
    useAutoImageHeight || !catalogSizes?.mainHeight ? null : catalogSizes.mainHeight;

  const outerClass = useAutoImageHeight
    ? `${styles.stripOuter} ${styles.fluidStripOuter}`
    : styles.stripOuter;
  const trackClass = useAutoImageHeight
    ? `${styles.stripTrack} ${styles.fluidStripTrack} ${display.autoScroll ? styles.stripTrackAuto : ""}`
    : `${styles.stripTrack} ${display.autoScroll ? styles.stripTrackAuto : ""}`;
  const itemClass = useAutoImageHeight
    ? `${styles.stripItem} ${styles.fluidStripItem}`
    : styles.stripItem;
  const imageClass = useAutoImageHeight
    ? `${styles.stripImage} ${styles.fluidStripImage}`
    : styles.stripImage;
  const resolvedObjectFit = useAutoImageHeight ? "contain" : objectFit;

  return (
    <div
      className={outerClass}
      style={stripHeight ? { height: stripHeight } : undefined}
    >
      <div
        ref={stripRef}
        className={trackClass}
        style={{ gap }}
        role="region"
        aria-label="Product image gallery"
      >
        {urls.map((url, index) => (
          <ProductImageLinkShell
            key={`${url}-${index}`}
            className={itemClass}
            isClickable={imageLinkActive}
            navigate={onImageNavigate}
            style={{
              width: `${slideWidthPercent}%`,
              ...itemFrameStyle,
            }}
          >
            <SizedCoverImg
              src={url}
              alt={`Product image ${index + 1}`}
              imgClassName={imageClass}
              objectFit={resolvedObjectFit}
              objectPosition={objectPosition}
              style={{
                borderRadius: itemFrameStyle.borderRadius || radius,
              }}
            />
          </ProductImageLinkShell>
        ))}
      </div>
    </div>
  );
}

const CoverImageBlock = ({
  section,
  productIndex,
  productData,
  popupData,
  activeStep = 0,
  forceCompactLayout = false,
  fixedCatalogSlot = false,
  preferCatalogHeroFrame = false,
  fillParentSlot = false,
}) => {
  const { isMobile } = useApp();
  const { isClickable, navigate } = useProductLink({
    popupData,
    productData,
    clickTarget: "image",
  });

  const blockCSS = useMemo(
    () => ({
      ...DEFAULT_BLOCK_CSS,
      ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
      ...parseCssStringToObject(
        section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
      ),
    }),
    [isMobile, section]
  );

  const displayLayout = section?.setting?.display?.layout;
  const display = useMemo(
    () => getCoverImageDisplay(section),
    [section, displayLayout, section?.setting?.display]
  );
  const catalogSizes = useMemo(
    () => getCoverImageCatalogSizes(section, isMobile),
    [section, isMobile, section?.setting?.display]
  );
  const mainCdnWidth = useMemo(
    () =>
      resolveShopifyCdnWidthFromDisplay(
        catalogSizes?.columnWidth || catalogSizes?.mainHeight,
        CATALOG_CARD_CDN_WIDTH
      ),
    [catalogSizes?.columnWidth, catalogSizes?.mainHeight]
  );
  const thumbCdnWidth = useMemo(
    () =>
      resolveShopifyCdnWidthFromDisplay(
        catalogSizes?.thumbWidth || catalogSizes?.thumbHeight,
        CATALOG_THUMB_CDN_WIDTH
      ),
    [catalogSizes?.thumbWidth, catalogSizes?.thumbHeight]
  );
  const urls = useMemo(
    () =>
      resolveCoverImageUrls(section, productData)
        .map((src) => normalizeImageSrc(src, mainCdnWidth))
        .filter(Boolean),
    [section, productData, mainCdnWidth]
  );

  const variantImageIndex = useVariantImageIndex(productData, productIndex, urls);
  const variantHeroSrc = useVariantHeroSrc(
    productData,
    productIndex,
    urls,
    mainCdnWidth
  );

  const borderRadiusPx = useMemo(() => {
    const fromBlock = parseBorderRadiusPx(blockCSS.borderRadius);
    if (fromBlock != null) return fromBlock;
    if (popupData) {
      return resolvePopupThemeBorderRadius(popupData, { activeStep, isMobile });
    }
    return 14;
  }, [blockCSS.borderRadius, popupData, activeStep, isMobile]);

  // Carousel cards only: square bottom image corners so the CTA sits flush.
  const squareBottomCorners = useMemo(() => {
    const sections = popupData?.schema?.[activeStep]?.section;
    const productSection = Array.isArray(sections)
      ? sections.find((s) => s?.type === POPUP_SECTIONS.PRODUCT)
      : null;
    const shell =
      productSection?.setting?.layout?.shell ||
      popupData?.generalSettings?.cartDrawerUpsellShell ||
      popupData?.generalSettings?.productShellLayout;
    return isCartDrawerVerticalShell(shell);
  }, [popupData, activeStep]);

  const imageFrameStyle = useMemo(
    () =>
      buildCoverImageFrameStyle(blockCSS, borderRadiusPx, {
        squareBottomCorners,
      }),
    [blockCSS, borderRadiusPx, squareBottomCorners]
  );

  const fluid = display.autoImageHeight === true;
  const layout = display.layout || COVER_IMAGE_LAYOUT.HERO_THUMBNAILS;
  const isSingleImageLayout = layout === COVER_IMAGE_LAYOUT.SINGLE;
  const isCompactThumbnail =
    !preferCatalogHeroFrame &&
    ((forceCompactLayout && isSingleImageLayout) ||
    (!fluid &&
      isSingleImageLayout &&
      catalogSizes?.mainHeight != null &&
      catalogSizes?.columnWidth != null));
  const objectFit = section?.objectFit || blockCSS.objectFit || "cover";
  const objectPosition = section?.objectPosition || blockCSS.objectPosition || "center";

  const rootStyle = useMemo(() => {
    const base = fluid
      ? (() => {
          const { height: _height, minHeight: _minHeight, ...rest } = blockCSS;
          return rest;
        })()
      : { ...blockCSS };
    if (fixedCatalogSlot) {
      const {
        borderWidth: _borderWidth,
        borderStyle: _borderStyle,
        borderColor: _borderColor,
        borderRadius: _borderRadius,
        ...rest
      } = base;
      return rest;
    }
    return base;
  }, [blockCSS, fluid, fixedCatalogSlot]);

  const isGalleryLayout =
    layout === COVER_IMAGE_LAYOUT.HERO_THUMBNAILS ||
    layout === COVER_IMAGE_LAYOUT.HORIZONTAL_SLIDER;
  const useCatalogGallery =
    fixedCatalogSlot && isGalleryLayout;

  const catalogHeroHeight = useMemo(() => {
    if (!fixedCatalogSlot) return null;
    return getProductRecommendationHeroImageHeight(section, isMobile);
  }, [fixedCatalogSlot, section, isMobile]);

  const catalogSlotHeight = useMemo(() => {
    if (!fixedCatalogSlot) return null;
    return getProductRecommendationImageSlotHeight(section, isMobile, {
      imageCount: urls.length,
      layout: display.layout,
    });
  }, [fixedCatalogSlot, section, isMobile, urls.length, display.layout]);

  const renderLayout = useCallback(() => {
    const activeLayout = display.layout || COVER_IMAGE_LAYOUT.HERO_THUMBNAILS;
    if (activeLayout === COVER_IMAGE_LAYOUT.HORIZONTAL_SLIDER) {
      return (
        <HorizontalStripLayout
          urls={urls}
          display={display}
          borderRadiusPx={borderRadiusPx}
          frameBorderStyle={imageFrameStyle}
          objectFit={objectFit}
          objectPosition={objectPosition}
          catalogSizes={catalogSizes}
          fluid={fluid}
          imageLinkActive={isClickable}
          onImageNavigate={navigate}
        />
      );
    }
    const singleImageOnly = activeLayout === COVER_IMAGE_LAYOUT.SINGLE;
    return (
      <HeroThumbnailsLayout
        urls={urls}
        display={display}
        borderRadiusPx={borderRadiusPx}
        frameBorderStyle={imageFrameStyle}
        objectFit={objectFit}
        objectPosition={objectPosition}
        variantImageIndex={variantImageIndex}
        catalogSizes={catalogSizes}
        showThumbnails={!singleImageOnly && urls.length > 1}
        fluid={fluid}
        slotConstrained={fixedCatalogSlot}
        catalogHeroHeight={catalogHeroHeight}
        catalogSlotHeight={catalogSlotHeight}
        preferCatalogHeroFrame={preferCatalogHeroFrame}
        heroLinkActive={isClickable}
        onHeroNavigate={navigate}
      />
    );
  }, [
    fluid,
    catalogSizes,
    display,
    urls,
    borderRadiusPx,
    imageFrameStyle,
    objectFit,
    objectPosition,
    variantImageIndex,
    fixedCatalogSlot,
    catalogHeroHeight,
    catalogSlotHeight,
    preferCatalogHeroFrame,
    isClickable,
    navigate,
  ]);

  // Vertical recommendation cards: fill parent + variant-image hover (product-page style).
  if (fillParentSlot) {
    const fillFrameStyle = {
      width: "100%",
      height: "100%",
      minHeight: 0,
      padding: 0,
      margin: 0,
      border: "none",
      borderRadius: 0,
      boxSizing: "border-box",
      overflow: "hidden",
      background: "transparent",
    };

    if (productData?.isDummy) {
      return (
        <div className={styles.catalogSlot} style={fillFrameStyle}>
          <CatalogDummyImagePlaceholder
            style={{ width: "100%", height: "100%", borderRadius: 0 }}
            iconSize="sm"
          />
        </div>
      );
    }

    const heroSrc = variantHeroSrc || urls[0] || "";
    return (
      <CatalogHoverImageSlot
        urls={urls}
        defaultSrc={heroSrc}
        imageFrameStyle={fillFrameStyle}
        objectFit="cover"
        objectPosition={objectPosition}
        isClickable={isClickable}
        navigate={navigate}
      />
    );
  }

  if (fixedCatalogSlot && useCatalogGallery) {
    if (productData?.isDummy) {
      return (
        <div className={`${styles.root} ${styles.catalogGalleryRoot}`}>
          <div className={styles.catalogSlot} style={imageFrameStyle}>
            <CatalogDummyImagePlaceholder
              style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
              iconSize="md"
            />
          </div>
        </div>
      );
    }

    return (
      <div className={`${styles.root} ${styles.catalogGalleryRoot}`} style={rootStyle}>
        {renderLayout()}
      </div>
    );
  }

  if (fixedCatalogSlot) {
    if (productData?.isDummy) {
      return (
        <div className={styles.catalogSlot} style={imageFrameStyle}>
          <CatalogDummyImagePlaceholder
            style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
            iconSize="sm"
          />
        </div>
      );
    }

    const heroSrc = variantHeroSrc || urls[0] || "";
    return (
      <CatalogHoverImageSlot
        urls={urls}
        defaultSrc={heroSrc}
        imageFrameStyle={imageFrameStyle}
        objectFit="cover"
        objectPosition={objectPosition}
        isClickable={isClickable}
        navigate={navigate}
      />
    );
  }

  if (isCompactThumbnail && urls.length <= 1) {
    const width = forceCompactLayout ? "100%" : catalogSizes.columnWidth;
    const height = catalogSizes.mainHeight;
    const heroSrc = productData?.isDummy ? "" : variantHeroSrc;

    return (
      <ProductImageLinkShell
        className={styles.compactExtensionThumb}
        isClickable={isClickable}
        navigate={navigate}
        style={{ width, height, ...imageFrameStyle }}
      >
        {heroSrc ? (
          <SizedCoverImg
            src={heroSrc}
            alt="Product"
            imgClassName={styles.compactExtensionThumbImg}
            objectFit={objectFit}
            objectPosition={objectPosition}
          />
        ) : productData?.isDummy ? (
          <CatalogDummyImagePlaceholder
            style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
            iconSize="sm"
          />
        ) : null}
      </ProductImageLinkShell>
    );
  }

  if (productData?.isDummy && (isCompactThumbnail || forceCompactLayout)) {
    const width = forceCompactLayout ? "100%" : catalogSizes?.columnWidth || "100%";
    const height = catalogSizes?.mainHeight || 88;
    return (
      <div
        className={styles.compactExtensionThumb}
        style={{
          width,
          height,
          ...imageFrameStyle,
        }}
      >
        <CatalogDummyImagePlaceholder
          style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
          iconSize="sm"
        />
      </div>
    );
  }

  if (productData?.isDummy) {
    return (
      <div
        className={fluid ? `${styles.root} ${styles.fluidRoot}` : styles.root}
        style={rootStyle}
      >
        <CatalogDummyImagePlaceholder
          className={fluid ? styles.fluidImageSkeleton : ""}
          style={{
            ...imageFrameStyle,
            width: "100%",
            ...(fluid ? {} : { aspectRatio: "1 / 1", minHeight: 64 }),
          }}
          iconSize="lg"
        />
      </div>
    );
  }

  return (
    <div
      className={fluid ? `${styles.root} ${styles.fluidRoot}` : styles.root}
      style={rootStyle}
    >
      {renderLayout()}
    </div>
  );
};

CoverImageBlock.propTypes = {
  productData: PropTypes.object,
  section: PropTypes.object.isRequired,
  productIndex: PropTypes.number,
  popupData: PropTypes.object,
  activeStep: PropTypes.number,
  forceCompactLayout: PropTypes.bool,
  fixedCatalogSlot: PropTypes.bool,
  preferCatalogHeroFrame: PropTypes.bool,
  fillParentSlot: PropTypes.bool,
};

export default memo(CoverImageBlock);
